import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { displayNameFromToken, lastNameFromToken, msUntilExpiry, rolesFromToken } from './jwt';
import { login as kcLogin, logout as kcLogout, readPersistedTokens, refresh as kcRefresh } from './keycloakAuth';

const AuthContext = createContext(null);

// Rafraîchit l'access token 30 s avant son expiration — même marge que `updateToken(30)` côté
// sipconfection-frontend (authGuard). Un token Keycloak par défaut dure quelques minutes ; sur
// une tablette d'atelier, mieux vaut anticiper largement plutôt que de couper la saisie en cours.
const REFRESH_MARGIN_MS = 30_000;

export function AuthProvider({ children }) {
  const [tokens, setTokens] = useState(() => readPersistedTokens());
  const [erreurAuth, setErreurAuth] = useState(null);
  const [pretAuthentifie, setPretAuthentifie] = useState(false);
  const refreshTimer = useRef(null);

  const planifierRefresh = useCallback((tk) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (!tk?.refreshToken) return;
    const delai = Math.max(msUntilExpiry(tk.accessToken) - REFRESH_MARGIN_MS, 1000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const nouveaux = await kcRefresh(tk.refreshToken);
        setTokens(nouveaux);
        planifierRefresh(nouveaux);
      } catch {
        // Refresh token expiré/révoqué (ex. session Keycloak fermée côté admin) : on redemande
        // une connexion explicite plutôt que de laisser l'app appeler l'API avec un jeton mort.
        setTokens(null);
      }
    }, delai);
  }, []);

  // Restauration d'une session persistée au démarrage de la PWA (tablette rallumée) : on tente
  // un refresh immédiat pour vérifier que le refresh token est toujours valide avant d'afficher
  // l'écran de liste, sinon on retombe proprement sur la connexion.
  //
  // ⚠️ SAUF si la tablette est hors-ligne à ce moment précis : un opérateur qui rallume l'app en
  // atelier sans réseau a des jetons parfaitement valides, seul le refresh a échoué faute de
  // réseau. Le déconnecter ici le renverrait à un écran de connexion... lui-même inutilisable
  // hors-ligne — l'enfermement que toute cette PWA existe pour éviter. On garde donc les jetons
  // persistés tels quels ; le refresh normal (planifié, ou déclenché par un 401) reprendra dès
  // que le réseau revient.
  useEffect(() => {
    let annule = false;
    (async () => {
      if (tokens?.refreshToken && navigator.onLine) {
        try {
          const nouveaux = await kcRefresh(tokens.refreshToken);
          if (!annule) {
            setTokens(nouveaux);
            planifierRefresh(nouveaux);
          }
        } catch {
          // Refus explicite du serveur (refresh token révoqué/expiré) : là, la déconnexion est
          // légitime — ce n'est pas un problème de réseau.
          if (!annule) setTokens(null);
        }
      } else if (tokens?.accessToken) {
        planifierRefresh(tokens);
      }
      if (!annule) setPretAuthentifie(true);
    })();
    return () => {
      annule = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username, password) => {
    setErreurAuth(null);
    try {
      const nouveaux = await kcLogin(username.trim(), password);
      setTokens(nouveaux);
      planifierRefresh(nouveaux);
      return true;
    } catch (err) {
      setErreurAuth(messageLogin(err));
      return false;
    }
  }, [planifierRefresh]);

  const logout = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const refreshToken = tokens?.refreshToken;
    setTokens(null);
    kcLogout(refreshToken);
  }, [tokens]);

  const getAccessToken = useCallback(() => tokens?.accessToken || null, [tokens]);

  /**
   * Force un refresh immédiat et renvoie le jeton frais — utilisé par le client HTTP quand un
   * appel API répond 401 alors que le minuteur de refresh n'a pas encore tourné (ex. horloge de
   * la tablette légèrement en retard sur celle de Keycloak).
   */
  const forcerRefresh = useCallback(async () => {
    if (!tokens?.refreshToken) return null;
    try {
      const nouveaux = await kcRefresh(tokens.refreshToken);
      setTokens(nouveaux);
      planifierRefresh(nouveaux);
      return nouveaux.accessToken;
    } catch {
      setTokens(null);
      return null;
    }
  }, [tokens, planifierRefresh]);

  const roles = useMemo(() => rolesFromToken(tokens?.accessToken), [tokens]);
  const nomAffiche = useMemo(() => displayNameFromToken(tokens?.accessToken), [tokens]);
  const nomFamille = useMemo(() => lastNameFromToken(tokens?.accessToken), [tokens]);

  const value = useMemo(() => ({
    connecte: !!tokens?.accessToken,
    pretAuthentifie,
    roles,
    nomAffiche,
    nomFamille,
    erreurAuth,
    login,
    logout,
    getAccessToken,
    forcerRefresh,
  }), [tokens, pretAuthentifie, roles, nomAffiche, nomFamille, erreurAuth, login, logout, getAccessToken, forcerRefresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé sous <AuthProvider>');
  return ctx;
}

/** `roles` requis (l'utilisateur en a besoin d'au moins un). Même convention que `auth.hasAnyRole`. */
export function hasAnyRole(userRoles, requis) {
  return requis.some((r) => userRoles.includes(r));
}

/** Rôles qui voient tout le parc de sous-traitants, sans restriction par `codeUsine`. Même
 * convention que ESPACE_ST_ROLES_GLOBAUX côté sipconfection-frontend. */
export const ROLES_PARC_COMPLET = ['OWNER', 'SUPERADMIN'];

function messageLogin(err) {
  if (err.status === 401 || err.keycloakError === 'invalid_grant') {
    return 'Identifiant ou mot de passe incorrect.';
  }
  if (err.status === 400 && err.keycloakError?.includes('direct access grants')) {
    return "Le client Keycloak n'autorise pas cette connexion (Direct Access Grants désactivé).";
  }
  if (err.status === undefined && /fetch|network/i.test(err.message || '')) {
    return 'Serveur injoignable. Vérifiez la connexion réseau de la tablette.';
  }
  return err.message || 'Connexion impossible.';
}
